import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import { assertNever } from "@redotech/util/type";
import {
  HsaAgeAnswer,
  HsaAnswer,
  HsaAnswerKind,
  HsaMultiSelectAnswer,
  HsaSingleSelectAnswer,
  HsaSingleSelectOtherAnswer,
  HsaTextAnswer,
} from "./hsa-answer";
import {
  HsaConditionExpression,
  HsaConditionLeaf,
  HsaConditionLeafKind,
  HsaDateConditionLeaf,
  HsaDateConditionOperator,
  HsaMultiSelectConditionLeaf,
  HsaMultiSelectConditionOperator,
  HsaNumericConditionLeaf,
  HsaNumericConditionOperator,
  HsaSingleSelectConditionLeaf,
  HsaSingleSelectConditionOperator,
  HsaTextConditionLeaf,
  HsaTextConditionOperator,
} from "./hsa-condition";
import {
  HsaContentNode,
  HsaFlow,
  HsaFlowNode,
  HsaFlowNodeType,
  HsaGroupNode,
} from "./hsa-flow";

// Customer answers keyed by questionHandle. Server-side state lives in the
// hsa_answer table; the walker is stateless and takes a snapshot of the
// answer set on each call.
export type HsaAnswerMap = Record<string, HsaAnswer>;

export enum HsaFlowRunnerErrorReason {
  MISSING_ANSWER = "missing_answer",
  ANSWER_KIND_MISMATCH = "answer_kind_mismatch",
  NEXT_ID_NOT_FOUND = "next_id_not_found",
  CYCLE_DETECTED = "cycle_detected",
}

export type HsaFlowRunnerResult =
  | { kind: "group"; node: HsaGroupNode }
  | { kind: "content"; node: HsaContentNode }
  | { kind: "done" }
  | { kind: "disqualified"; nodeId: string; reason: string }
  | {
      kind: "error";
      nodeId: string;
      reason: HsaFlowRunnerErrorReason;
      message: string;
    };

// A renderable step (group or content screen) on the active path.
export type HsaStepNode =
  | { kind: "group"; node: HsaGroupNode }
  | { kind: "content"; node: HsaContentNode };

/**
 * The active path for an answer set: renderable steps in walk order (frontier
 * included) plus the walk's ending result. Conditions/knockouts aren't steps.
 */
export interface HsaFlowPath {
  steps: HsaStepNode[];
  result: HsaFlowRunnerResult;
}

// Walks the flow from rootId via id lookup and returns the next group the
// customer needs to fill in given the answers submitted so far, or signals
// completion. Stateless — caller passes the current answer set on every
// call. Resume-on-reload comes for free: the same answers + same flow
// always resolve to the same current location.
//
// A group is "passable" when every one of its questionHandles has an answer
// in the map. Auto-advances through fully-answered groups and through
// condition nodes (evaluating the expression). Returns the first group
// whose questions are not all answered, or "done" if the walker reaches
// a null edge.
//
// Cycle detection runs as a safety net even though the validator forbids
// cycles at authoring time. Missing-id refs and answer-kind mismatches
// surface as error results rather than silent traversal failures.
export function nextHsaGroup(
  flow: HsaFlow,
  answers: HsaAnswerMap,
  now: Date = new Date(),
): HsaFlowRunnerResult {
  return walkHsaFlowPath(flow, answers, now).result;
}

/**
 * Walks the whole active path, collecting every renderable step it passes
 * (frontier included); the steps power step-through neighbor lookups.
 */
export function walkHsaFlowPath(
  flow: HsaFlow,
  answers: HsaAnswerMap,
  now: Date = new Date(),
): HsaFlowPath {
  const steps: HsaStepNode[] = [];
  if (flow.rootId === null) {
    return { steps, result: { kind: "done" } };
  }
  const nodeById = new Map<string, HsaFlowNode>();
  for (const node of flow.nodes) {
    nodeById.set(node.nodeId, node);
  }
  const visited = new Set<string>();
  let currentId: string | null = flow.rootId;
  while (currentId !== null) {
    if (visited.has(currentId)) {
      return {
        steps,
        result: {
          kind: "error",
          nodeId: currentId,
          reason: HsaFlowRunnerErrorReason.CYCLE_DETECTED,
          message: `Cycle reached at node "${currentId}".`,
        },
      };
    }
    visited.add(currentId);
    const node = nodeById.get(currentId);
    if (!node) {
      return {
        steps,
        result: {
          kind: "error",
          nodeId: currentId,
          reason: HsaFlowRunnerErrorReason.NEXT_ID_NOT_FOUND,
          message: `Node id "${currentId}" does not match any node in the flow.`,
        },
      };
    }
    switch (node.nodeType) {
      case HsaFlowNodeType.GROUP: {
        steps.push({ kind: "group", node });
        const allAnswered = node.questionHandles.every(
          (handle) => answers[handle] !== undefined,
        );
        if (!allAnswered) {
          return { steps, result: { kind: "group", node } };
        }
        currentId = node.nextId;
        break;
      }
      case HsaFlowNodeType.CONDITION: {
        const result = evaluateExpression(
          node.nodeId,
          node.condition,
          answers,
          now,
        );
        if (result.kind === "error") {
          return { steps, result };
        }
        currentId = result.value ? node.matchingNextId : node.nonMatchingNextId;
        break;
      }
      case HsaFlowNodeType.KNOCKOUT: {
        return {
          steps,
          result: {
            kind: "disqualified",
            nodeId: node.nodeId,
            reason: node.reason,
          },
        };
      }
      case HsaFlowNodeType.CONTENT: {
        steps.push({ kind: "content", node });
        const ack = answers[node.contentHandle];
        if (ack?.kind !== HsaAnswerKind.ACKNOWLEDGEMENT) {
          return { steps, result: { kind: "content", node } };
        }
        currentId = node.nextId;
        break;
      }
      default: {
        assertNever(node);
      }
    }
  }
  return { steps, result: { kind: "done" } };
}

// The handle a step is addressed by on the wire (group vs content handle).
export function hsaStepHandle(step: HsaStepNode): string {
  switch (step.kind) {
    case "group": {
      return step.node.groupHandle;
    }
    case "content": {
      return step.node.contentHandle;
    }
    default: {
      return assertNever(step);
    }
  }
}

/**
 * The step after `currentStepHandle` (step-through advance). Off-path handles
 * fall back to the walk's own result so a stale client re-syncs.
 */
export function hsaStepAfter(
  path: HsaFlowPath,
  currentStepHandle: string,
): HsaFlowRunnerResult {
  const index = path.steps.findIndex(
    (step) => hsaStepHandle(step) === currentStepHandle,
  );
  if (index === -1) {
    return path.result;
  }
  const next = path.steps[index + 1];
  return next ?? path.result;
}

/**
 * The step before `currentStepHandle`, or null when the current step is first
 * or not on the path (nothing to go back to).
 */
export function hsaStepBefore(
  path: HsaFlowPath,
  currentStepHandle: string,
): HsaStepNode | null {
  const index = path.steps.findIndex(
    (step) => hsaStepHandle(step) === currentStepHandle,
  );
  if (index <= 0) {
    return null;
  }
  return path.steps[index - 1] ?? null;
}

// Whether the step addressed by `stepHandle` has a predecessor on the path.
export function hsaStepHasPrevious(
  path: HsaFlowPath,
  stepHandle: string,
): boolean {
  return path.steps.findIndex((step) => hsaStepHandle(step) === stepHandle) > 0;
}

type EvalResult =
  | { kind: "value"; value: boolean }
  | {
      kind: "error";
      nodeId: string;
      reason: HsaFlowRunnerErrorReason;
      message: string;
    };

function evaluateExpression(
  nodeId: string,
  expression: HsaConditionExpression,
  answers: HsaAnswerMap,
  now: Date,
): EvalResult {
  const blockResults: boolean[] = [];
  for (const block of expression.conditionBlocks) {
    const result = evaluateBlock(nodeId, block, answers, now);
    if (result.kind === "error") {
      return result;
    }
    blockResults.push(result.value);
  }
  return {
    kind: "value",
    value: combine(expression.conjunction, blockResults),
  };
}

function evaluateBlock(
  nodeId: string,
  block: { conjunction: ConjunctionMode; conditions: HsaConditionLeaf[] },
  answers: HsaAnswerMap,
  now: Date,
): EvalResult {
  const leafResults: boolean[] = [];
  for (const condition of block.conditions) {
    const result = evaluateLeaf(nodeId, condition, answers, now);
    if (result.kind === "error") {
      return result;
    }
    leafResults.push(result.value);
  }
  return { kind: "value", value: combine(block.conjunction, leafResults) };
}

// Empty AND is vacuously true; empty OR is vacuously false. Standard
// propositional-logic semantics.
function combine(mode: ConjunctionMode, results: boolean[]): boolean {
  switch (mode) {
    case ConjunctionMode.AND:
      return results.every(Boolean);
    case ConjunctionMode.OR:
      return results.some(Boolean);
    default:
      assertNever(mode);
  }
}

function evaluateLeaf(
  nodeId: string,
  leaf: HsaConditionLeaf,
  answers: HsaAnswerMap,
  now: Date,
): EvalResult {
  const answer = answers[leaf.questionHandle];
  if (!answer) {
    return {
      kind: "error",
      nodeId,
      reason: HsaFlowRunnerErrorReason.MISSING_ANSWER,
      message: `Condition references question "${leaf.questionHandle}" but no answer is present.`,
    };
  }
  switch (leaf.leafKind) {
    case HsaConditionLeafKind.NUMERIC: {
      if (answer.kind !== HsaAnswerKind.AGE) {
        return kindMismatch(nodeId, leaf, answer);
      }
      return { kind: "value", value: evaluateNumeric(leaf, answer) };
    }
    case HsaConditionLeafKind.TEXT: {
      if (answer.kind !== HsaAnswerKind.TEXT) {
        return kindMismatch(nodeId, leaf, answer);
      }
      return { kind: "value", value: evaluateText(leaf, answer) };
    }
    case HsaConditionLeafKind.DATE: {
      if (answer.kind !== HsaAnswerKind.TEXT) {
        return kindMismatch(nodeId, leaf, answer);
      }
      return { kind: "value", value: evaluateDate(leaf, answer, now) };
    }
    case HsaConditionLeafKind.SINGLE_SELECT: {
      if (
        answer.kind !== HsaAnswerKind.SINGLE_SELECT &&
        answer.kind !== HsaAnswerKind.SINGLE_SELECT_OTHER
      ) {
        return kindMismatch(nodeId, leaf, answer);
      }
      return { kind: "value", value: evaluateSingleSelect(leaf, answer) };
    }
    case HsaConditionLeafKind.MULTI_SELECT: {
      if (answer.kind !== HsaAnswerKind.MULTI_SELECT) {
        return kindMismatch(nodeId, leaf, answer);
      }
      return { kind: "value", value: evaluateMultiSelect(leaf, answer) };
    }
    default: {
      assertNever(leaf);
    }
  }
}

function kindMismatch(
  nodeId: string,
  leaf: HsaConditionLeaf,
  answer: HsaAnswer,
): EvalResult {
  return {
    kind: "error",
    nodeId,
    reason: HsaFlowRunnerErrorReason.ANSWER_KIND_MISMATCH,
    message: `Answer for "${leaf.questionHandle}" has kind "${answer.kind}"; condition leaf kind "${leaf.leafKind}" expects a different answer shape.`,
  };
}

function evaluateNumeric(
  leaf: HsaNumericConditionLeaf,
  answer: HsaAgeAnswer,
): boolean {
  switch (leaf.operator) {
    case HsaNumericConditionOperator.EQUALS:
      return answer.value === leaf.value;
    case HsaNumericConditionOperator.NOT_EQUALS:
      return answer.value !== leaf.value;
    case HsaNumericConditionOperator.LESS_THAN:
      return answer.value < leaf.value;
    case HsaNumericConditionOperator.LESS_THAN_OR_EQUAL:
      return answer.value <= leaf.value;
    case HsaNumericConditionOperator.GREATER_THAN:
      return answer.value > leaf.value;
    case HsaNumericConditionOperator.GREATER_THAN_OR_EQUAL:
      return answer.value >= leaf.value;
    default:
      assertNever(leaf.operator);
  }
}

// For operators requiring a value, we trust the schema and validator; if
// the value is missing here, return false (don't take the branch) rather
// than throw. This degrades gracefully for malformed flows that slipped
// through.
function evaluateText(
  leaf: HsaTextConditionLeaf,
  answer: HsaTextAnswer,
): boolean {
  const { value } = leaf;
  switch (leaf.operator) {
    case HsaTextConditionOperator.EQUALS:
      return value !== undefined && answer.text === value;
    case HsaTextConditionOperator.NOT_EQUALS:
      return value !== undefined && answer.text !== value;
    case HsaTextConditionOperator.CONTAINS:
      return value !== undefined && answer.text.includes(value);
    case HsaTextConditionOperator.NOT_CONTAINS:
      return value !== undefined && !answer.text.includes(value);
    case HsaTextConditionOperator.MATCHES_REGEX: {
      if (value === undefined) {
        return false;
      }
      try {
        return new RegExp(value).test(answer.text);
      } catch {
        return false;
      }
    }
    case HsaTextConditionOperator.IS_EMPTY:
      return answer.text.length === 0;
    case HsaTextConditionOperator.IS_NOT_EMPTY:
      return answer.text.length > 0;
    default:
      assertNever(leaf.operator);
  }
}

// Malformed / non-ISO date answers can't be aged, so they never take the
// branch — the flow validator guarantees a real date upstream.
function evaluateDate(
  leaf: HsaDateConditionLeaf,
  answer: HsaTextAnswer,
  now: Date,
): boolean {
  const age = ageInWholeYears(answer.text, now);
  if (age === null) {
    return false;
  }
  switch (leaf.operator) {
    case HsaDateConditionOperator.AGE_LESS_THAN:
      return age < leaf.value;
    case HsaDateConditionOperator.AGE_AT_LEAST:
      return age >= leaf.value;
    default:
      assertNever(leaf.operator);
  }
}

export function ageInWholeYears(isoDate: string, now: Date): number | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const monthNow = now.getUTCMonth() + 1;
  let age = now.getUTCFullYear() - year;
  if (monthNow < month || (monthNow === month && now.getUTCDate() < day)) {
    age -= 1;
  }
  return age;
}

function evaluateSingleSelect(
  leaf: HsaSingleSelectConditionLeaf,
  answer: HsaSingleSelectAnswer | HsaSingleSelectOtherAnswer,
): boolean {
  const inSet = leaf.optionValues.includes(answer.optionValue);
  switch (leaf.operator) {
    case HsaSingleSelectConditionOperator.IS_ANY_OF:
      return inSet;
    case HsaSingleSelectConditionOperator.IS_NONE_OF:
      return !inSet;
    default:
      assertNever(leaf.operator);
  }
}

function evaluateMultiSelect(
  leaf: HsaMultiSelectConditionLeaf,
  answer: HsaMultiSelectAnswer,
): boolean {
  const answerSet = new Set(answer.optionValues);
  const optionValues = leaf.optionValues ?? [];
  const leafSet = new Set(optionValues);
  switch (leaf.operator) {
    case HsaMultiSelectConditionOperator.INCLUDES_ANY:
      return optionValues.some((v) => answerSet.has(v));
    case HsaMultiSelectConditionOperator.INCLUDES_ALL:
      return optionValues.every((v) => answerSet.has(v));
    case HsaMultiSelectConditionOperator.INCLUDES_NONE:
      return !optionValues.some((v) => answerSet.has(v));
    case HsaMultiSelectConditionOperator.IS_EXACTLY:
      return (
        answerSet.size === leafSet.size &&
        optionValues.every((v) => answerSet.has(v))
      );
    case HsaMultiSelectConditionOperator.IS_EMPTY:
      return answer.optionValues.length === 0;
    case HsaMultiSelectConditionOperator.IS_NOT_EMPTY:
      return answer.optionValues.length > 0;
    default:
      assertNever(leaf.operator);
  }
}
